/**
 * Shared chat time helpers — day dividers + live-updating relative time.
 * A single module-level ticker drives every <RelativeTime> on the page so we
 * never spin up one interval per message (chat lists can hold hundreds).
 */
import React, { useEffect, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";

// ---- Shared 30s ticker (one interval for the whole app) --------------------
const _subscribers = new Set();
let _timer = null;
let _now = Date.now();

function _ensureTimer() {
  if (_timer) return;
  _now = Date.now();
  _timer = setInterval(() => {
    _now = Date.now();
    _subscribers.forEach((fn) => fn(_now));
  }, 30000);
}

export function useNowTick() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    _subscribers.add(setNow);
    _ensureTimer();
    return () => {
      _subscribers.delete(setNow);
      if (_subscribers.size === 0 && _timer) {
        clearInterval(_timer);
        _timer = null;
      }
    };
  }, []);
  return now;
}

// ---- Formatters ------------------------------------------------------------
// Relative for the first 24h ("just now", "2m ago", "3h ago"); clock time
// (e.g. "05:23 PM") for anything older — the day is carried by the divider.
export function timeAgo(dateStr, now = Date.now()) {
  if (!dateStr) return "";
  const t = new Date(dateStr).getTime();
  if (isNaN(t)) return "";
  const diff = Math.max(0, now - t);
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return new Date(dateStr).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export function sameDay(a, b) {
  if (!a || !b) return false;
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

// "Today" / "Yesterday" / "Mar 12" / "Mar 12, 2025" (year only when not current)
export function dayDividerLabel(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const diffDays = Math.round((startOf(now) - startOf(d)) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(
    [],
    sameYear
      ? { month: "short", day: "numeric" }
      : { month: "short", day: "numeric", year: "numeric" }
  );
}

// ---- Components ------------------------------------------------------------
export function RelativeTime({ date, className = "", testid }) {
  const now = useNowTick();
  if (!date) return null;
  return (
    <span className={className} title={new Date(date).toLocaleString()} data-testid={testid}>
      {timeAgo(date, now)}
    </span>
  );
}

export function ChatDateDivider({ date, messages, idPrefix = "msg" }) {
  const [open, setOpen] = useState(false);
  if (!date) return null;

  const jumpToDate = (picked) => {
    setOpen(false);
    if (!picked || !messages) return;
    const target = new Date(picked.getFullYear(), picked.getMonth(), picked.getDate()).getTime();
    // Messages are chronological — jump to the first one on or after the day.
    const hit = messages.find((m) => {
      if (!m.created_at) return false;
      const d = new Date(m.created_at);
      return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() >= target;
    });
    const el = hit && document.getElementById(`${idPrefix}-${hit.id}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-[#F54800]", "rounded-md");
      setTimeout(() => el.classList.remove("ring-2", "ring-[#F54800]", "rounded-md"), 1500);
    } else {
      toast("No messages on or after that date");
    }
  };

  const label = (
    <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
      {dayDividerLabel(date)}
    </span>
  );

  return (
    <div className="flex items-center gap-2 py-1.5 select-none" data-testid="chat-date-divider">
      <div className="flex-1 h-px bg-slate-200" />
      {messages ? (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" className="rounded-full px-2 py-0.5 hover:bg-slate-100 transition-colors cursor-pointer" title="Jump to a date" data-testid="chat-date-divider-btn">
              {label}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="center" data-testid="chat-date-calendar">
            <Calendar mode="single" onSelect={jumpToDate} defaultMonth={new Date(date)} disabled={{ after: new Date() }} />
          </PopoverContent>
        </Popover>
      ) : (
        label
      )}
      <div className="flex-1 h-px bg-slate-200" />
    </div>
  );
}
